import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { languages, subscription, user as userApi } from '../../services/api';
import { useConfirm } from '../../hooks/useConfirm';
import AddLanguageModal from './AddLanguageModal';
import LoginScreen from '../Auth/LoginScreen';
import TrialStatusBanner from '../Trial/TrialStatusBanner';
import SubscriptionPaywallModal from '../Trial/SubscriptionPaywallModal';
import ChatWidget from '../Messages/ChatWidget';
import ConfirmModal from '../Common/ConfirmModal';
import './HomePage.css';

const LANGUAGE_FLAGS = {
  es: '🇪🇸',
  fr: '🇫🇷',
  de: '🇩🇪',
  it: '🇮🇹',
  pt: '🇵🇹',
  ja: '🇯🇵',
  ko: '🇰🇷',
  zh: '🇨🇳',
  ru: '🇷🇺',
  ar: '🇸🇦',
};

const HomePage = () => {
  const { user, logout, isStudent, isTeacher, isAuthenticated, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [userLanguages, setUserLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddLanguageModal, setShowAddLanguageModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [copiedLanguageId, setCopiedLanguageId] = useState(null);
  const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm();

  // Check if user's trial has expired
  const isTrialExpired = () => {
    if (!user || user.hasPremium) return false;
    if (!user.trialEndsAt) return false;

    const trialEnd = new Date(user.trialEndsAt);
    const now = new Date();
    return trialEnd <= now;
  };

  // Check if user is in active trial
  const isInTrial = () => {
    if (!user || user.hasPremium) return false;
    if (!user.trialEndsAt) return false;

    const trialEnd = new Date(user.trialEndsAt);
    const now = new Date();
    return trialEnd > now;
  };

  // Redirect teachers to teacher dashboard
  useEffect(() => {
    if (isAuthenticated && isTeacher()) {
      navigate('/teacher-dashboard');
    }
  }, [isAuthenticated, isTeacher, navigate]);

  // Handle Stripe checkout success/failure
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');

    if (sessionId) {
      console.log('✅ Payment successful! Session ID:', sessionId);

      // Refresh user data from backend to get updated subscription status
      const refreshAndCleanup = async () => {
        console.log('🔄 Refreshing user data from backend...');
        await refreshUser();

        // Remove session_id param from URL
        setSearchParams({});

        console.log('✅ Subscription complete! User data refreshed.');
      };

      refreshAndCleanup();
    } else if (canceled === 'true') {
      console.log('❌ Payment canceled by user');
      alert('Payment was canceled. You can try again anytime from the banner or menu.');

      // Remove canceled param
      setSearchParams({});
    }
  }, [searchParams, refreshUser, setSearchParams]);

  useEffect(() => {
    console.log('🔍 HomePage useEffect triggered, user:', user);
    console.log('🔑 localStorage authToken:', localStorage.getItem('authToken'));
    console.log('👤 localStorage user:', localStorage.getItem('user'));

    if (user && isStudent()) {
      console.log('✅ User exists and is a student, calling fetchUserLanguages');
      fetchUserLanguages();

      // Check if trial expired and show paywall
      if (isTrialExpired()) {
        console.log('❌ Trial expired, showing paywall');
        setShowPaywallModal(true);
      }
    } else {
      console.log('❌ No user or not a student, skipping fetchUserLanguages');
    }
  }, [user]);

  const fetchUserLanguages = async () => {
    try {
      console.log('📞 fetchUserLanguages called');
      console.log('🔑 Token in localStorage before API call:', localStorage.getItem('authToken'));
      setLoading(true);
      const response = await languages.getAll();
      console.log('✅ languages.getAll() succeeded:', response.data);
      setUserLanguages(response.data.languages || []);
    } catch (error) {
      console.error('❌ Failed to fetch languages:', error);
      console.error('❌ Error response:', error.response);
      setUserLanguages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSubscribe = async () => {
    setSubscribeLoading(true);
    try {
      // Get current URL for success/cancel redirects
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/home?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${baseUrl}/home?canceled=true`;

      console.log('🛒 Creating Stripe checkout session...');
      const response = await subscription.createCheckoutSession({
        successUrl,
        cancelUrl,
      });

      console.log('✅ Checkout session created:', response.data.sessionId);
      console.log('🔗 Redirecting to Stripe:', response.data.sessionUrl);

      // Redirect to Stripe Checkout
      window.location.href = response.data.sessionUrl;
    } catch (error) {
      console.error('❌ Failed to create checkout session:', error);

      // Show user-friendly error messages
      if (error.response?.status === 400) {
        alert(error.response.data.error || 'You already have an active subscription.');
      } else {
        alert('Failed to start checkout. Please try again or contact support.');
      }

      setSubscribeLoading(false);
    }
  };

  const handleStartMeeting = (language) => {
    navigate(`/video-meeting?languageId=${language.id}`);
  };

  const handleViewFlashcards = (language) => {
    navigate(`/progress?languageId=${language.id}`);
  };

  const handleLanguageAdded = () => {
    fetchUserLanguages();
    setShowAddLanguageModal(false);
  };

  const handleDeleteLanguage = async (languageId) => {
    const confirmed = await confirm({
      title: 'Delete Language',
      message: 'Are you sure you want to delete this language? This will also delete all associated flashcards and transcriptions.',
      confirmText: 'Delete',
      confirmStyle: 'danger'
    });

    if (!confirmed) return;

    try {
      await languages.delete(languageId);
      fetchUserLanguages();
    } catch (error) {
      console.error('Failed to delete language:', error);
      alert('Failed to delete language. Please try again.');
    }
  };

  const handleCopyMeetingLink = async (language) => {
    try {
      console.log('📞 Fetching meeting room for language:', language.id);

      // Fetch the meeting room for this language
      const response = await userApi.getVideoMeetingRoom(language.id);
      console.log('✅ Meeting room response:', response.data);

      // Build app URL that goes through GuestMeetingPage for teacher authentication
      // Format: https://teacha-web-dev.duckdns.org/meet/{roomId}
      const roomId = response.data.roomId;
      const appUrl = window.location.origin;
      const meetingUrl = `${appUrl}/meet/${roomId}`;

      // Copy to clipboard
      await navigator.clipboard.writeText(meetingUrl);

      // Show success feedback
      setCopiedLanguageId(language.id);

      // Reset the success message after 2 seconds
      setTimeout(() => {
        setCopiedLanguageId(null);
      }, 2000);

      console.log('✅ Copied meeting URL to clipboard:', meetingUrl);
    } catch (error) {
      console.error('❌ Failed to copy meeting link:', error);
      console.error('❌ Error response:', error.response);

      if (error.response?.status === 403 || error.response?.status === 401) {
        alert('Your subscription has expired. Please upgrade to share meeting links.');
      } else {
        alert('Failed to copy meeting link. Please try again.');
      }
    }
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  // Show loading while fetching user data
  if (!user) {
    return <div className="loading-container">Loading...</div>;
  }

  return (
    <div className="home-container">
      {/* Trial Status Banner - only for students in active trial */}
      {isStudent() && isInTrial() && (
        <TrialStatusBanner
          trialEndsAt={user.trialEndsAt}
          onUpgrade={handleSubscribe}
        />
      )}

      {/* Subscription Paywall Modal - blocks access when trial expires */}
      {isStudent() && showPaywallModal && (
        <SubscriptionPaywallModal
          onSubscribe={handleSubscribe}
          loading={subscribeLoading}
        />
      )}

      <header className="app-header">
        <div className="header-content">
          <div className="app-title">
            <span className="app-icon-small">🌍</span>
            <h1>Teacha</h1>
          </div>

          <nav className="header-nav">
            <button
              className="nav-button"
              onClick={() => navigate('/find-teachers')}
              title="Find teachers to practice with"
            >
              <span className="nav-icon">🔍</span>
              <span className="nav-label">Find Teachers</span>
            </button>
            <button
              className="nav-button"
              onClick={() => navigate('/progress')}
              title="View your progress and transcriptions"
            >
              <span className="nav-icon">📊</span>
              <span className="nav-label">Progress</span>
            </button>
            <button
              className="nav-button"
              onClick={() => navigate('/flashcards')}
              title="Review your flashcards"
            >
              <span className="nav-icon">🃏</span>
              <span className="nav-label">Flashcards</span>
            </button>
          </nav>

          <div className="header-user">
            <button
              className="user-button"
              onClick={() => setShowMenu(!showMenu)}
            >
              {user?.name || 'User'}
              <span className="dropdown-icon">▼</span>
            </button>

            {showMenu && (
              <div className="dropdown-menu">
                <button onClick={() => navigate('/profile')}>
                  Profile
                </button>
                {!user?.hasPremium && (
                  <button onClick={handleSubscribe}>
                    Upgrade to Premium
                  </button>
                )}
                <button onClick={handleLogout}>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="main-content">
        <section className="welcome-section">
          <div className="how-it-works">
            <h2>🎯 How Teacha Works</h2>
            <div className="feature-grid">
              <div className="feature-item">
                <span className="feature-number">1</span>
                <div className="feature-content">
                  <h3>Schedule 1:1 Sessions</h3>
                  <p>Join video meetings with native speakers. Share your meeting link with instructors to practice conversation.</p>
                </div>
              </div>
              <div className="feature-item">
                <span className="feature-number">2</span>
                <div className="feature-content">
                  <h3>Auto-Generated Flashcards</h3>
                  <p>Every conversation is recorded, transcribed, and automatically converted into flashcards with key vocabulary and phrases.</p>
                </div>
              </div>
              <div className="feature-item">
                <span className="feature-number">3</span>
                <div className="feature-content">
                  <h3>Track Your Progress</h3>
                  <p>Review flashcards with spaced repetition, monitor conversation stats, and watch your proficiency improve over time.</p>
                </div>
              </div>
            </div>
            {user?.hasPremium ? (
              <p className="status-badge premium-badge">✨ Premium Member</p>
            ) : isInTrial() ? (
              <p className="status-badge trial-badge">🎉 Free Trial Active</p>
            ) : null}
          </div>
        </section>

        <section className="languages-section">
          <div className="section-header">
            <h3>My Languages</h3>
            <button
              className="btn-add-language"
              onClick={() => setShowAddLanguageModal(true)}
            >
              + Add Language
            </button>
          </div>

          {loading ? (
            <div className="loading">Loading your languages...</div>
          ) : userLanguages.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🌐</div>
              <h4>No languages yet</h4>
              <p>Add a language to start learning!</p>
              <button
                className="btn-primary"
                onClick={() => setShowAddLanguageModal(true)}
              >
                Add Your First Language
              </button>
            </div>
          ) : (
            <div className="language-cards">
              {userLanguages.map((lang) => (
                <div
                  key={lang.id}
                  className="language-card"
                  onClick={() => handleViewFlashcards(lang)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="language-header">
                    <div className="language-title">
                      <span className="language-flag">
                        {LANGUAGE_FLAGS[lang.languageCode] || '🌍'}
                      </span>
                      <div>
                        <h4>{lang.languageName}</h4>
                        <p className="language-native">{lang.nativeName}</p>
                      </div>
                    </div>
                    <button
                      className="btn-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteLanguage(lang.id);
                      }}
                      title="Delete language"
                    >
                      ×
                    </button>
                  </div>

                  <div className="language-stats">
                    <div className="stat">
                      <span className="stat-label">Proficiency</span>
                      <span className="stat-value">{lang.proficiencyLevel}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Flashcards</span>
                      <span className="stat-value">{lang.flashcardCount || 0}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Conversations</span>
                      <span className="stat-value">{lang.conversationCount || 0}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Study Time</span>
                      <span className="stat-value">{lang.totalStudyTimeMinutes || 0} min</span>
                    </div>
                  </div>

                  <div className="language-actions">
                    <button
                      className="btn-meeting"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartMeeting(lang);
                      }}
                    >
                      🎥 Join Meeting
                    </button>
                    <button
                      className="btn-copy-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyMeetingLink(lang);
                      }}
                      title="Copy meeting link to share with your instructor"
                    >
                      {copiedLanguageId === lang.id ? '✓ Copied!' : '🔗 Copy Link'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {showAddLanguageModal && (
        <AddLanguageModal
          onClose={() => setShowAddLanguageModal(false)}
          onLanguageAdded={handleLanguageAdded}
        />
      )}

      {/* Chat Widget - Floating in bottom right */}
      <ChatWidget />

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        confirmStyle={confirmState.confirmStyle}
      />
    </div>
  );
};

export default HomePage;
